import React, { useState } from 'react'
import { withLocalize, Translate } from 'react-localize-redux'
import { makeStyles } from '@material-ui/core/styles'
import CssBaseline from '@material-ui/core/CssBaseline'
import {
  AppBar,
  Toolbar,
  Box,
  MenuItem,
  Button,
  Typography,
  Grid,
  Menu,
  IconButton,
} from '@material-ui/core'
import { setLocale, getLocale } from 'utils/localstorage'
import PropTypes from 'prop-types'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { SubLink } from 'containers'
import ExitToAppIcon from '@material-ui/icons/ExitToApp'
import PaymentIcon from '@material-ui/icons/Payment'
import LanguageIcon from '@material-ui/icons/Language'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import { track } from 'utils/segmentTracking'
import Logo from 'assets/images/coachbetter_Logo_Black.svg'
import { auth as authModule } from 'redux/modules'
import {
  checkIsAfterDateTime,
  diffDate,
  displayDateForEventPage,
} from 'utils/formatDate'
import { LogoImage } from '../../../containers/NavBar/styledComponents'
import {
  CLUB_SUBSCRIPTION_ROUTE,
  LOGOUT_ROUTE,
  TEAMS_ROUTE,
} from 'constants/routes'
import languages from 'constants/languages'

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  appBar: {
    backgroundColor: theme.header.background,
    minHeight: 100,
    display: 'flex',
    justifyContent: 'center',
  },
  backLink: {
    color: `${theme.palette.common.black} !important`,
  },
  select: {
    minWidth: '70px',
    marginRight: '10px',
  },
  formControl: {
    '& .MuiSelect-icon': {
      top: `calc(50% - 14px)`,
    },
    '& .MuiFormLabel-root.Mui-focused': {
      color: theme.palette.primary.main,
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
  },
  content: {
    flexGrow: 1,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    paddingTop: 100,
    width: 1,
    margin: '0 auto',
  },
}))

const TeamsHeader = ({
  children,
  clubTheme,
  setActiveLanguage,
  user,
  currentClub,
  updateUser,
  isBillingPageActive,
}) => {
  const classes = useStyles()
  const [mainSubscription, setMainSubscription] = useState(null)
  const [addOnSubscriptions, setAddOnSubscriptions] = useState([])

  const [language, changeLanguage] = React.useState(getLocale('locale'))
  setLocale(language)

  const [anchorEl, setAnchorEl] = useState(null)

  const handleLanguageMenuOpen = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleLanguageMenuClose = () => {
    setAnchorEl(null)
  }
  const handleLanguageMenuChange = (lang) => {
    changeLanguage(lang)
    setActiveLanguage(lang)
    setLocale(lang)

    updateUser(user.uuid, {
      language: lang,
      preventToastNotification: true,
    })

    handleLanguageMenuClose()

    // Segment tracking
    track('WClick_TO_Language', {
      category: 'language',
      userUuid: user.uuid,
      userEmail: user.email,
      currentClub: currentClub.name,
      currentLanguage: lang,
    })
  }

  return (
    <>
      <CssBaseline />
      <Box className={classes.root}>
        <AppBar
          position="fixed"
          color="inherit"
          className={classes.appBar}
          elevation={0}
        >
          <Toolbar>
            <Box display="flex" alignItems="center">
              {isBillingPageActive && (
                <SubLink to={TEAMS_ROUTE} className={classes.backLink}>
                  <IconButton edge="start" color="inherit" size="small">
                    <ArrowBackIcon fontSize="inherit" />
                  </IconButton>
                </SubLink>
              )}
              <SubLink to="/">
                <LogoImage
                  src={clubTheme && clubTheme.logo ? clubTheme.logo : Logo}
                  alt="Coachbetter"
                />
              </SubLink>
            </Box>
            <Box
              m="auto"
              display="flex"
              flexDirection="column"
              alignItems="center"
            >
              <Box>{currentClub.name}</Box>
              <Box display="flex" alignItems="center">
                {mainSubscription && (
                  <Box display="flex" alignItems="center">
                    <Box>
                      <Typography color="primary">Plan -</Typography>
                    </Box>
                    <Box ml={0.5}>
                      <Typography>
                        Coachbetter {mainSubscription?.plan_display_name}
                      </Typography>
                    </Box>
                  </Box>
                )}
                {/* Trial ending data */}
                {mainSubscription?.status === 'trialing' &&
                  mainSubscription.trial_ends_at &&
                  !mainSubscription?.ends_at &&
                  !mainSubscription?.past_due &&
                  (() => {
                    const daysLeft =
                      diffDate(mainSubscription.trial_ends_at, new Date()) + 1

                    if (daysLeft === 0) return null

                    return (
                      <Typography color={daysLeft > 7 ? 'primary' : 'error'}>
                        <i>
                          &nbsp;(
                          <Translate id="plans.trial-ends-on" />{' '}
                          {displayDateForEventPage(
                            mainSubscription.trial_ends_at
                          )}
                          )
                        </i>
                      </Typography>
                    )
                  })()}

                {/* Subscription ending data */}
                {mainSubscription?.ends_at &&
                  !mainSubscription?.past_due &&
                  (() => {
                    if (
                      checkIsAfterDateTime(new Date(), mainSubscription.ends_at)
                    )
                      return (
                        <Typography color="error">
                          <i>
                            &nbsp;(
                            <Translate id="plans.expired-on" />{' '}
                            {displayDateForEventPage(mainSubscription.ends_at)})
                          </i>
                        </Typography>
                      )

                    return (
                      <Typography color="error">
                        <i>
                          &nbsp;(
                          <Translate id="plans.ends-on" />{' '}
                          {displayDateForEventPage(mainSubscription.ends_at)})
                        </i>
                      </Typography>
                    )
                  })()}

                {mainSubscription?.status === 'active' &&
                  !mainSubscription?.ends_at && (
                    <Typography color="primary">
                      <i>
                        &nbsp;(
                        <Translate id="movePlayers.active" />)
                      </i>
                    </Typography>
                  )}

                {mainSubscription?.past_due && (
                  <Typography color="error">
                    <i>
                      &nbsp;(
                      <Translate id="plans.past-due-info" />)
                    </i>
                  </Typography>
                )}

                {mainSubscription && addOnSubscriptions.length > 0 && (
                  <Box mx={1}>|</Box>
                )}

                {/* Show Add-on subscriptions with comma(,) separated as its possible to have multiple addons */}
                {addOnSubscriptions.length > 0 && (
                  <Box display="flex" alignItems="center">
                    <Box>
                      <Typography color="primary">Add-on -</Typography>
                    </Box>
                    <Box ml={0.5}>
                      <Typography>
                        {addOnSubscriptions
                          .map((sub) => sub.plan_display_name)
                          .join(', ')}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
            <Box display="flex" alignItems="center">
              {!isBillingPageActive && currentClub?.can?.includes('manage-billing') && (
                  <Box>
                    <SubLink
                      to={CLUB_SUBSCRIPTION_ROUTE.populateURLParams({
                        clubUuid: currentClub.uuid,
                      })}
                    >
                      <Button size="small">
                        <Grid
                          container
                          direction="column"
                          justifyContent="center"
                          alignItems="center"
                        >
                          <PaymentIcon fontSize="small" />
                          <Typography variant="caption">Billing</Typography>
                        </Grid>
                      </Button>
                    </SubLink>
                  </Box>
                )}
              {isBillingPageActive && (
                <Box>
                  <Button size="small" onClick={handleLanguageMenuOpen}>
                    <Grid
                      container
                      direction="column"
                      justifyContent="center"
                      alignItems="center"
                    >
                      <LanguageIcon fontSize="small" />
                      <Typography variant="caption">
                        {language.toUpperCase()}
                      </Typography>
                    </Grid>
                  </Button>

                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleLanguageMenuClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                    getContentAnchorEl={null} // <- Important to avoid conflict
                  >
                    {languages.map(({ code, name }) => (
                      <MenuItem
                        key={code}
                        onClick={() => handleLanguageMenuChange(code)}
                      >
                        {name}
                      </MenuItem>
                    ))}
                  </Menu>
                </Box>
              )}
              <Box>
                <SubLink to={LOGOUT_ROUTE}>
                  <Button size="small">
                    <Grid
                      container
                      direction="column"
                      justifyContent="center"
                      alignItems="center"
                    >
                      <ExitToAppIcon fontSize="small" />
                      <Typography variant="caption">
                        <Translate id="navbar.logout" />
                      </Typography>
                    </Grid>
                  </Button>
                </SubLink>
              </Box>
            </Box>
          </Toolbar>
        </AppBar>
        <Box className={classes.content}>{children}</Box>
      </Box>
    </>
  )
}

TeamsHeader.defaultProps = {
  children: null,
  clubTheme: null,
  currentClub: {},
  isBillingPageActive: false,
}

TeamsHeader.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.element.isRequired,
    PropTypes.arrayOf(PropTypes.element.isRequired).isRequired,
  ]),
  updateUser: PropTypes.func.isRequired,
  setActiveLanguage: PropTypes.func.isRequired,
  clubTheme: PropTypes.shape({
    logo: PropTypes.string,
  }),
  currentClub: PropTypes.shape({
    uuid: PropTypes.string,
    name: PropTypes.string,
    type: PropTypes.string,
    subscriptions: PropTypes.arrayOf(
      PropTypes.shape({
        plan_display_name: PropTypes.string,
        status: PropTypes.string,
        trial_ends_at: PropTypes.string,
        ends_at: PropTypes.string,
        past_due: PropTypes.bool,
        cancellable: PropTypes.bool,
        updatable: PropTypes.bool,
      })
    ),
    can: PropTypes.arrayOf(PropTypes.string),
  }),
  user: PropTypes.shape({
    id: PropTypes.number.isRequired,
    uuid: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
  }).isRequired,
  isBillingPageActive: PropTypes.bool,
}

export default compose(
  connect(
    ({ auth }) => ({
      user: auth.user,
    }),
    {
      updateUser: authModule.updateUser,
    }
  ),
  withLocalize,
  withRouter
)(TeamsHeader)
